<template>
  <div v-if="style" :style="style" ref="lavContainer"/>
</template>

<script>
import lottie from "lottie-web";
import axios from "axios";

/**
 * Wrapper arround lottie-web. Used for svg animations.
 */
export default {
  props: {
     /**
     * json data filepath location in `public/` folder
     * <p>
     * Note: root is alrady in public. Don't append `/` at the start of your path
     */
    json: {
      type: String,
      required: true
    },
     /**
     * Sets the animation speed 
     */
    speed: {
      type: Number,
      required: false,
      default: 1
    },
     /**
     * Width (px/%)
     */
    width: {
      type: String,
      required: false,
      default: '100%'
    },
     /**
     * Height (px/%)
     */
    height: {
      type: String,
      required: false,
      default: '100%'
    },
     /**
     * Loop animation
     */
    loop: {
      type:Boolean,
      required: false,
      default: true
    },
     /**
     * Play automatically
     */
    autoPlay: {
      type:Boolean,
      required: false,
      default: true
    },
     /**
     * Minimum loop delay in milliseconds. 
     */
    loopDelayMin: {
      type: Number,
      required: false,
      default: 0
    },
    /**
     * Maximum loop delay in milliseconds. 
     */
    loopDelayMax: {
      type: Number,
      required: false,
      default: 0
    },
  },
  data: () => ({
    rendererSettings: {
      scaleMode: "centerCrop",
      clearCanvas: true,
      progressiveLoad: false,
      hideOnTransparent: true
    },
    anim: null,
    style: null
  }),
  mounted() {
    this.init();
  },
  methods: {
    /**
   * @public
   * Loads json data from the public folder using an axios GET
   * @param {string} path 
   * @return {object} Json data object
   *
   * @example
   *
   *     loadJsonData('animations/tools.json')
   */
    async loadJsonData(path) {
      return await axios.get("/" + path).then(response => {
        return response.data;
      });
    },
     /**
     * @public
     * Instantiates the lottie-web library with the specified parameters
     */
    async init() {
      this.style = {
        width: this.width,
        height: this.height,
        overflow: "hidden",
        margin: "0 auto"
      };

      let jsonData = await this.loadJsonData(this.json);
      this.anim = lottie.loadAnimation({
        container: this.$refs.lavContainer,
        renderer: "svg",
        loop: this.loop,
        autoplay: this.autoPlay,
        animationData: jsonData,
        rendererSettings: this.rendererSettings
      });
      this.anim.setSpeed(this.speed);
      if (this.loopDelayMin > 0) {
        this.anim.loop = false;
        this.anim.autoplay = false;
        this.executeLoop();
      }
      /**
       * Returns the lottie-web animation handler for manual animation control.
       *
       * @event AnimControl
       * @type {event}
       */
      this.$emit("AnimControl", this.anim);
    },
     /**
     * @public
     * Returns a random number within the specified range
     * @param {number} min 
     * @param {number} max
     * @return {number}
     *
     * @example
     *
     *     getRandomInt(100, 200)
     */
    getRandomInt(min, max) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
    },
    /**
     * @public
     * Plays the animation based on loopDelayMin and loopDelayMax
     */
    executeLoop() {
      this.anim.play();
      setTimeout(() => {
        this.anim.stop();
        this.executeLoop();
      }, this.getRandomInt(this.loopDelayMin, this.loopDelayMax == 0? this.loopDelayMin : this.loopDelayMax));
    }
  },
  watch: {
    json: function(newVal, oldVal) {
      this.init();
    }
  }
};
</script>

<style>
</style>
<docs>

</docs>